import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { Navigate, Outlet, createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { isSignedIn } from './auth';
import { loadConfig } from './config';
import { RaceLayout } from './director/RaceLayout';
import { CoursePage } from './director/CoursePage';
import { DirectorsPage } from './director/DirectorsPage';
import { LoginPage } from './director/LoginPage';
import { RaceDayPage } from './director/RaceDayPage';
import { RacesPage } from './director/RacesPage';
import { ReviewPage } from './director/ReviewPage';
import { RosterPage } from './director/RosterPage';
import { TeamPage } from './director/TeamPage';
import { DocsPage } from './public/DocsPage';
import { HelpPage } from './public/HelpPage';
import { LandingPage } from './public/LandingPage';
import { SignUpPage } from './public/SignUpPage';
import { PublicResultsPage } from './public/PublicResultsPage';
import { StationPairPage } from './station/StationPairPage';
import { StationRecordPage } from './station/StationRecordPage';
import './styles.css';

// The camera page pulls in the video encoder; only phones need it.
const StationSetupPage = lazy(() => import('./station/StationSetupPage').then((m) => ({ default: m.StationSetupPage })));
const StationCheckPage = lazy(() => import('./station/StationCheckPage').then((m) => ({ default: m.StationCheckPage })));

/** Signed in: your races. Otherwise: what Splitline is. */
function Home() {
  return isSignedIn() ? <RacesPage /> : <LandingPage />;
}

function RequireDirector() {
  return isSignedIn() ? <Outlet /> : <Navigate to="/login" replace />;
}

const router = createBrowserRouter([
  { path: '/', element: <Home /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/signup', element: <SignUpPage /> },
  { path: '/results/:raceId', element: <PublicResultsPage /> },
  { path: '/help', element: <HelpPage /> },
  { path: '/docs', element: <DocsPage /> },
  { path: '/docs/:slug', element: <DocsPage /> },
  {
    element: <RequireDirector />,
    children: [
      { path: '/admin/directors', element: <DirectorsPage /> },
      {
        path: '/races/:raceId',
        element: <RaceLayout />,
        children: [
          { index: true, element: <RaceDayPage /> },
          { path: 'roster', element: <RosterPage /> },
          { path: 'course', element: <CoursePage /> },
          { path: 'review', element: <ReviewPage /> },
          { path: 'team', element: <TeamPage /> },
        ],
      },
    ],
  },
  { path: '/station', element: <StationPairPage /> },
  {
    path: '/station/setup',
    element: (
      <Suspense fallback={<div className="center-page">Loading camera…</div>}>
        <StationSetupPage />
      </Suspense>
    ),
  },
  { path: '/station/record', element: <StationRecordPage /> },
  {
    path: '/station/check',
    element: (
      <Suspense fallback={<div className="center-page">Loading…</div>}>
        <StationCheckPage />
      </Suspense>
    ),
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

loadConfig().then(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
});
